'use client';

import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';

import ResponsiveLayout from '@/components/apps/layouts/responsive/Layout';
import Home from '@/components/apps/pages/home/Home';

type Props = {
  industrialCases: ReadonlyArray<IndustrialCaseSummary>;
};

const IS_MAIN_POPUP_ENABLED =
  process.env.NEXT_PUBLIC_MAIN_POPUP_ENABLED === 'true';

/**
 * ### HomePageClient
 *
 * Hydrates the home page shell with the server-resolved customer cases while
 * keeping the popup controller client-only.
 *
 * @param props Published Industrial cases prepared by the route
 * @returns The client-side home page shell
 */
export default function HomePageClient({ industrialCases }: Props) {
  const [HomePopupController, setHomePopupController] =
    useState<ComponentType | null>(null);

  // 홈만 캔버스가 어둡다. 스타일 자체는 globals.css 에 정적으로 있고 클래스만 토글한다.
  useEffect(() => {
    document.documentElement.classList.add('home-dark-canvas');
    return () => {
      document.documentElement.classList.remove('home-dark-canvas');
    };
  }, []);

  useEffect(() => {
    if (!IS_MAIN_POPUP_ENABLED) return;

    let isActive = true;

    void import('./HomePopupController')
      .then(({ default: Controller }) => {
        if (isActive) setHomePopupController(() => Controller);
      })
      .catch((error: unknown) => {
        console.error('Failed to load the main popup controller', error);
      });

    return () => {
      isActive = false;
    };
  }, []);

  return (
    <>
      {HomePopupController && <HomePopupController />}
      <ResponsiveLayout mobilePadding={{ top: '0', bottom: '0' }}>
        <Home industrialCases={industrialCases} />
      </ResponsiveLayout>
    </>
  );
}
